import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerTrendRow {
  sellerKey: string
  sellerLabel: string
  currentOrderCount: number
  currentAverageTicketCents: number
  previousOrderCount: number
  previousAverageTicketCents: number | null
  variationPercent: number | null
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const parseDateInput = (value: string): Date | null => {
  if (!value) {
    return null
  }

  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return date
}

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

const getDaySpanInclusive = (startDate: string, finalDate: string): number => {
  const start = parseDateInput(startDate)
  const end = parseDateInput(finalDate)

  if (!start || !end) {
    return 0
  }

  const diffMs = end.getTime() - start.getTime()
  if (diffMs < 0) {
    return 0
  }

  return Math.floor(diffMs / 86400000) + 1
}

const buildPreviousPeriodFilters = (
  filters: SellerTicketAverageFilters
): SellerTicketAverageFilters | null => {
  const daySpan = getDaySpanInclusive(filters.startDate, filters.finalDate)
  if (daySpan <= 0) {
    return null
  }

  const currentStart = parseDateInput(filters.startDate)
  if (!currentStart) {
    return null
  }

  const previousStart = addDays(currentStart, -daySpan)
  const previousEnd = addDays(currentStart, -1)

  return {
    ...filters,
    startDate: toDateInputValue(previousStart),
    finalDate: toDateInputValue(previousEnd),
  }
}

const buildSellerTrendRows = (
  currentReport: SellerTicketAverageReport | null,
  previousReport: SellerTicketAverageReport | null
): SellerTrendRow[] => {
  if (!currentReport) {
    return []
  }

  const previousBySeller = new Map(
    (previousReport?.rows || []).map((row) => [row.sellerKey, row])
  )

  const rows = currentReport.rows.map((row) => {
    const previous = previousBySeller.get(row.sellerKey)
    const previousAverageTicketCents = previous?.averageTicketCents ?? null
    const variationPercent =
      previousAverageTicketCents && previousAverageTicketCents > 0
        ? ((row.averageTicketCents - previousAverageTicketCents) / previousAverageTicketCents) * 100
        : null

    return {
      sellerKey: row.sellerKey,
      sellerLabel: row.sellerLabel,
      currentOrderCount: row.orderCount,
      currentAverageTicketCents: row.averageTicketCents,
      previousOrderCount: previous?.orderCount || 0,
      previousAverageTicketCents,
      variationPercent,
    }
  })

  return rows.sort((left, right) => {
    const leftValue = left.variationPercent ?? Number.NEGATIVE_INFINITY
    const rightValue = right.variationPercent ?? Number.NEGATIVE_INFINITY
    return rightValue - leftValue
  })
}

const trendColor = (variationPercent: number | null): string => {
  if (variationPercent === null) {
    return 'text-gray-600'
  }

  if (variationPercent > 0) {
    return 'text-success'
  }

  if (variationPercent < 0) {
    return 'text-danger'
  }

  return 'text-gray-700'
}

const trendLabel = (variationPercent: number | null): string => {
  if (variationPercent === null) {
    return '-'
  }

  const signal = variationPercent > 0 ? '+' : ''
  return `${signal}${percentFormatter.format(variationPercent)}%`
}

const exportToCSV = (filename: string, rows: SellerTrendRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Pedidos atuais;Ticket médio atual;Pedidos anteriores;Ticket médio anterior;Variação (%)\n'

  rows.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.currentOrderCount};${row.currentAverageTicketCents / 100};${row.previousOrderCount};${row.previousAverageTicketCents ? row.previousAverageTicketCents / 100 : ''};${row.variationPercent === null ? '' : row.variationPercent.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: SellerTrendRow[]): void => {
  const data = rows.map((row, index) => ({
    Posição: index + 1,
    Vendedor: row.sellerLabel,
    'Pedidos atuais': row.currentOrderCount,
    'Ticket médio atual': row.currentAverageTicketCents / 100,
    'Pedidos anteriores': row.previousOrderCount,
    'Ticket médio anterior': row.previousAverageTicketCents ? row.previousAverageTicketCents / 100 : null,
    'Variação (%)': row.variationPercent === null ? null : row.variationPercent.toFixed(1),
  }))

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Tendencia')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (
  filename: string,
  rows: SellerTrendRow[],
  currentReport: SellerTicketAverageReport | null,
  previousReport: SellerTicketAverageReport | null
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Tendência por Vendedor', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Comparativo Consolidado', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(
    `Período atual: ${currentReport?.summary.totalOrders || 0} pedidos | Período anterior: ${previousReport?.summary.totalOrders || 0} pedidos`,
    margin,
    yPosition
  )
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Pedidos atuais', 'Ticket atual', 'Pedidos anteriores', 'Ticket anterior', 'Variação (%)']],
    body: rows.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.currentOrderCount,
      `${row.currentAverageTicketCents / 100}`,
      row.previousOrderCount,
      row.previousAverageTicketCents ? `${row.previousAverageTicketCents / 100}` : '-',
      row.variationPercent === null ? '-' : row.variationPercent.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const TicketTrendBySellerReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [currentReport, setCurrentReport] = useState<SellerTicketAverageReport | null>(null)
  const [previousReport, setPreviousReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)

      const previousFilters = buildPreviousPeriodFilters(filters)
      if (!previousFilters) {
        throw new Error('Período inválido para comparação')
      }

      const [currentData, previousData] = await Promise.all([
        loadSellerTicketAverageReport(filters),
        loadSellerTicketAverageReport(previousFilters),
      ])

      setCurrentReport(currentData)
      setPreviousReport(previousData)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const trendRows = useMemo(
    () => buildSellerTrendRows(currentReport, previousReport),
    [currentReport, previousReport]
  )

  const topGrowth = useMemo(() => {
    const candidates = trendRows.filter((row) => row.variationPercent !== null)
    if (!candidates.length) {
      return null
    }

    return [...candidates].sort(
      (left, right) => (right.variationPercent || 0) - (left.variationPercent || 0)
    )[0]
  }, [trendRows])

  const topDrop = useMemo(() => {
    const candidates = trendRows.filter((row) => row.variationPercent !== null)
    if (!candidates.length) {
      return null
    }

    return [...candidates].sort(
      (left, right) => (left.variationPercent || 0) - (right.variationPercent || 0)
    )[0]
  }, [trendRows])

  const previousFilters = useMemo(() => buildPreviousPeriodFilters(filters), [filters])

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Tendência por Vendedor',
            path: '/seller/reports/ticket-trend-seller',
            isActive: true,
          },
        ]}
      >
        Relatório de Tendência por Vendedor
      </PageTitle>

      <ReportShell
        title='Tendência de Ticket Médio por Vendedor'
        description='Compara ticket médio do período atual com o período imediatamente anterior de mesma duração.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Tendencia-Ticket-${timestamp}.csv`, trendRows)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Tendencia-Ticket-${timestamp}.xlsx`, trendRows)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Tendencia-Ticket-${timestamp}.pdf`, trendRows, currentReport, previousReport)
            }} disabled={loading || trendRows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-3 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
            <div className='col-12'>
              <div className='text-gray-600 fs-7'>
                Comparativo anterior:{' '}
                <span className='fw-semibold text-dark'>
                  {previousFilters
                    ? `${previousFilters.startDate} até ${previousFilters.finalDate}`
                    : 'Período inválido'}
                </span>
              </div>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores analisados</div>
                <div className='fs-2hx fw-bold text-primary'>
                  {(currentReport?.rows.length || 0).toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior alta</div>
                <div className='fs-5 fw-bold text-success'>
                  {topGrowth ? <SellerIdentity label={topGrowth.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-success'>
                  {topGrowth ? trendLabel(topGrowth.variationPercent) : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior queda</div>
                <div className='fs-5 fw-bold text-danger'>
                  {topDrop ? <SellerIdentity label={topDrop.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-danger'>
                  {topDrop ? trendLabel(topDrop.variationPercent) : '-'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Comparativo por vendedor</span>
              <span className='text-gray-600 mt-1'>
                Ticket médio atual versus período anterior equivalente.
              </span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : trendRows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos atual</th>
                      <th className='text-end'>Ticket atual</th>
                      <th className='text-end'>Pedidos anterior</th>
                      <th className='text-end'>Ticket anterior</th>
                      <th className='text-end'>Variação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trendRows.map((row) => (
                      <tr key={row.sellerKey}>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold'>{row.currentOrderCount}</td>
                        <td className='text-end fw-bold text-primary'>
                          {formatCurrency(row.currentAverageTicketCents)}
                        </td>
                        <td className='text-end'>{row.previousOrderCount}</td>
                        <td className='text-end'>
                          {row.previousAverageTicketCents !== null
                            ? formatCurrency(row.previousAverageTicketCents)
                            : '-'}
                        </td>
                        <td className={`text-end fw-bold ${trendColor(row.variationPercent)}`}>
                          {trendLabel(row.variationPercent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { TicketTrendBySellerReportPage }